import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Badge, List, notification, Spin } from "antd";
import moment from "moment";
import {
  useShopifyShopSync,
  useShopifyInstallLink,
  useShopifyStoreIndex,
} from "../../../../../hooks/integrations/shopify";

const StoreIndexTable = ({ organizationID, isOrderSync = true }) => {
  const [reInstalling, setReInstalling] = useState(false);
  const { storeIndex, fetchStores } = useShopifyStoreIndex();
  const { shopSync, syncShop } = useShopifyShopSync();
  const { installLink, generateInstallLink } = useShopifyInstallLink();

  const reportShopFetchErrors = () => {
    if (storeIndex.error) {
      notification.error({
        message: "Unable to fetch Shopify shops",
        description: storeIndex.error,
      });
    }
  };

  const reportSyncErrors = () => {
    if (shopSync.error) {
      notification.error({
        message: "Unable to sync Shopify shop",
        description: shopSync.error,
      });
    }
  };

  const reportSyncComplete = () => {
    if (!shopSync.data) return;
    if (shopSync.data.shop && !shopSync.data.errors) {
      notification.success({
        message: `Successfully synced ${shopSync.data.shop.name}`,
      });
    }

    if (shopSync.data.errors && shopSync.data.errors.length > 0) {
      notification.warning({
        message: "Sync completed with errors",
        description: shopSync.data.errors,
      });
    }
  };

  const redirectToReInstallLink = () => {
    if (installLink.error) setReInstalling(false);
    if (installLink.data && installLink.data.url) {
      window.location.href = installLink.data.url;
      return;
    }
  };

  // eslint-disable-next-line
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchStores({ organization_id: organizationID });
  }, []);
  useEffect(reportShopFetchErrors, [storeIndex.error]);
  useEffect(reportSyncErrors, [shopSync.error]);
  useEffect(reportSyncComplete, [shopSync.data]);
  useEffect(redirectToReInstallLink, [installLink]);

  const renderLoading = () => {
    if (storeIndex.loading) return <Spin />;
  };

  const shopSyncOnClick = (e, shop) => {
    e.preventDefault();
    syncShop(shop.name, organizationID);
  };

  const shopSyncLink = (shop) => (
    <button className="sync-shop-button" onClick={(e) => shopSyncOnClick(e, shop)}>
      Sync Orders
    </button>
  );

  const shopReInstallOnClick = (e, shop) => {
    e.preventDefault();
    setReInstalling(true);
    generateInstallLink(shop.name.trim());
  };

  const shopReInstallLink = (shop) => {
    return (
      <a href="#/" onClick={(e) => shopReInstallOnClick(e, shop)}>
        Reactivate
      </a>
    );
  };

  const shopActions = (shop) => (shop.active ? [shopSyncLink(shop)] : [shopReInstallLink(shop)]);

  const shopInstallStatus = (shop) =>
    shop.active ? <Badge color="green" text="Active" /> : <Badge color="orange" text="Inactive" />;

  const renderList = () => {
    if (storeIndex.data && storeIndex.data) {
      const spinText = () => {
        if (reInstalling) return "Redirecting to Shopify...";
        if (shopSync.loading) return "Syncing orders...";
      };

      return (
        <Spin spinning={shopSync.loading || reInstalling} tip={spinText()}>
          <List
            itemLayout="horizontal"
            dataSource={storeIndex.data}
            renderItem={(shop) => (
              <List.Item actions={shopActions(shop)} key={shop.id}>
                <List.Item.Meta
                  title={shop.name}
                  description={
                    <span>
                      {shopInstallStatus(shop)}
                      {` - Created ${moment(shop.created_at).fromNow()}`}
                    </span>
                  }
                />
              </List.Item>
            )}
          />
        </Spin>
      );
    }
  };

  return (
    <div className="shopify-install-list">
      <h3 className="header">Your Shopify Stores</h3>
      {renderLoading()}
      {renderList()}
    </div>
  );
};

StoreIndexTable.propTypes = {
  organizationID: PropTypes.string,
  isOrderSync: PropTypes.bool,
};

export default StoreIndexTable;
